import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Shield, 
  Upload, 
  Download, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  FileText,
  Calendar,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En cours de traitement', class: 'bg-amber-100 text-amber-700', icon: Clock },
  validated: { label: 'Valide', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Refuse', class: 'bg-red-100 text-red-700', icon: XCircle },
};

export const Attestations = () => {
  const [attestations, setAttestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API}/attestations/status`, { credentials: 'include' }),
        fetch(`${API}/attestations/history`, { credentials: 'include' }),
      ]);

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
      }

      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setAttestations(historyData);
      }
    } catch (error) {
      console.error('Failed to fetch attestation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptes');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/attestations/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Attestation deposee avec succes');
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors du depot');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du depot');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadAttestation = async (attestationId, filename) => {
    try {
      const response = await fetch(`${API}/attestations/${attestationId}/download`, {
        credentials: 'include',
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'attestation.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Attestation telechargee');
      } else {
        toast.error('Erreur lors du telechargement');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    }
  };

  const validAttestations = attestations.filter(a => a.status === 'validated');
  const pendingAttestations = attestations.filter(a => a.status === 'pending');
  const rejectedAttestations = attestations.filter(a => a.status === 'rejected');

  // Calculate days until expiration
  const daysUntilExpiration = status?.expires_at 
    ? Math.max(0, Math.ceil((new Date(status.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="attestations-title">
          Attestations de vigilance
        </h1>
        <p className="text-slate-500 mt-1">
          Deposez et suivez vos attestations de vigilance URSSAF
        </p>
      </div>

      {/* Status Card */}
      <Card className={`border-0 shadow-sm ${
        status?.blocked ? 'border-l-4 border-l-red-500 bg-red-50' : 
        daysUntilExpiration !== null && daysUntilExpiration < 15 ? 'border-l-4 border-l-amber-500 bg-amber-50' :
        'border-l-4 border-l-emerald-500 bg-emerald-50'
      }`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${
              status?.blocked ? 'bg-red-100' : 
              daysUntilExpiration !== null && daysUntilExpiration < 15 ? 'bg-amber-100' :
              'bg-emerald-100'
            }`}>
              <Shield className={`w-6 h-6 ${
                status?.blocked ? 'text-red-600' : 
                daysUntilExpiration !== null && daysUntilExpiration < 15 ? 'text-amber-600' :
                'text-emerald-600'
              }`} />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-lg">
                {status?.blocked ? 'Attestation requise' : 'Attestation valide'}
              </h2>
              <p className="text-slate-600 mt-1">{status?.message}</p>
              
              {status?.expires_at && !status?.blocked && (
                <div className="flex items-center gap-2 mt-3 text-sm">
                  <Calendar className="w-4 h-4 text-slate-500" />
                  <span>
                    Expire le {new Date(status.expires_at).toLocaleDateString('fr-FR')}
                    {daysUntilExpiration !== null && (
                      <span className={`ml-2 font-medium ${
                        daysUntilExpiration < 15 ? 'text-amber-600' : 'text-slate-600'
                      }`}>
                        ({daysUntilExpiration} jours restants)
                      </span>
                    )}
                  </span>
                </div>
              )}

              {status?.status === 'pending' && (
                <div className="flex items-center gap-2 mt-3">
                  <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                  <span className="text-amber-700">Votre attestation est en cours de verification...</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-600" />
            Deposer une attestation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
            <input
              type="file"
              ref={fileInputRef}
              accept=".pdf"
              className="hidden"
              onChange={handleFileChange}
              data-testid="attestation-file-input"
            />
            <Shield className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">
              Deposez votre attestation de vigilance URSSAF au format PDF
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || status?.status === 'pending'}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="upload-attestation-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Choisir un fichier
                </>
              )}
            </Button>
            {status?.status === 'pending' && (
              <p className="text-sm text-amber-600 mt-3">
                Une attestation est deja en cours de traitement
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Validees</p>
              <p className="text-xl font-bold text-emerald-600">{validAttestations.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En attente</p>
              <p className="text-xl font-bold text-amber-600">{pendingAttestations.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Refusees</p>
              <p className="text-xl font-bold text-red-600">{rejectedAttestations.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History Table */}
      <Card className="border-0 shadow-sm" data-testid="attestations-history-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Historique des attestations
          </CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Document</TableHead>
                <TableHead className="font-semibold">Date de depot</TableHead>
                <TableHead className="font-semibold">Statut</TableHead>
                <TableHead className="font-semibold">Expiration</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : attestations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Aucune attestation deposee
                  </TableCell>
                </TableRow>
              ) : (
                attestations.map((att) => {
                  const StatusIcon = statusConfig[att.status]?.icon || Clock;
                  return (
                    <TableRow key={att.attestation_id} className="hover:bg-slate-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <span className="font-medium">{att.filename}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {new Date(att.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[att.status]?.class} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[att.status]?.label}
                        </Badge>
                        {att.rejection_comment && (
                          <p className="text-xs text-red-600 mt-1">{att.rejection_comment}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {att.expires_at 
                          ? new Date(att.expires_at).toLocaleDateString('fr-FR')
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadAttestation(att.attestation_id, att.filename)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Telecharger
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
